import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import TelegramLoginButton from 'telegram-login-button';
import { QRCodeSVG } from 'qrcode.react';
import moment from 'moment';

import Input from '../../../components/site/Input/Input';
import Loading from '../../../components/site/Loading/Loading';
import { userUpdate } from '../../../redux/actions/user/userUpdate';
import { resetUserUpdate } from '../../../redux/slices/user.slice';
import { userAuth } from '../../../redux/actions/user/userAuth';
import { userTelegram } from '../../../redux/actions/user/userTelegram';
import { userTelegramLinkToken } from '../../../redux/actions/user/userTelegramLinkToken';
import { userProfile } from '../../../redux/actions/user/userProfile';
import SubscriptionSection from './SubscriptionSection';

const Avatar = ({ name, email }) => {
  const initials = name
    ? name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
    : (email || '?')[0].toUpperCase();
  return (
    <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-600 tw-flex tw-items-center tw-justify-center tw-text-white tw-text-base tw-font-bold tw-flex-shrink-0 tw-shadow-md">
      {initials}
    </div>
  );
};

const Card = ({ children, className = '' }) => (
  <div className={`tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-overflow-hidden ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children }) => (
  <div className="tw-px-5 tw-py-3.5 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5">
    {children}
  </div>
);

const AccountPage = () => {
  const defaultValues = { email: '', fio: '', phone_number: '' };
  const form = useForm({ defaultValues });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { tariffPayment: { loading: tariffPaymentLoading } } = useSelector((s) => s.tariff);
  const {
    userAuth: { data: userData },
    userProfile: { data: userProfileData },
    userTelegram: { data: userTelegramData },
    userUpdate: { data: userUpdateData, loading: userUpdateLoading },
    userTelegramLinkToken: { data: linkTokenData, loading: linkTokenLoading },
  } = useSelector((s) => s.user);

  useEffect(() => {
    if (userData) {
      form.setValue('email', userData?.email || '');
      form.setValue('fio',
        `${userData?.first_name ?? ''}${userData?.first_name && userData?.last_name ? ' ' : ''}${userData?.last_name ?? ''}`
      );
    }
  }, [userData]);

  useEffect(() => {
    if (userProfileData) form.setValue('phone_number', userProfileData?.phone_number || '');
  }, [userProfileData]);

  useEffect(() => {
    if (userUpdateData) {
      dispatch(resetUserUpdate());
      toast.success('Данные профиля обновлены');
      dispatch(userAuth());
    }
  }, [userUpdateData]);

  useEffect(() => {
    if (userTelegramData) {
      toast.success('Телеграм привязан');
      dispatch(userProfile());
    }
  }, [userTelegramData]);

  const onSubmit = (data) => {
    const fio = data?.fio?.split(' ');
    dispatch(userUpdate({ first_name: fio[0], last_name: fio[1] ?? '' }));
  };

  const fullName = `${userData?.first_name ?? ''} ${userData?.last_name ?? ''}`.trim();
  const tariffInfo = userProfileData?.tariff_info;
  const endDate = userProfileData?.end_date;
  const hasTelegram = !!userProfileData?.telegram_user_id;

  return (
    <>
      <Helmet><title>Профиль</title></Helmet>

      <div className="tw-max-w-2xl tw-mx-auto tw-px-2 tw-space-y-4">

        {/* ── ROW 1: two columns ── */}
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-5 tw-gap-4">

          {/* LEFT — profile + form (wider) */}
          <Card className="sm:tw-col-span-3">
            {/* Profile strip */}
            <div className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
              <Avatar name={fullName} email={userData?.email} />
              <div className="tw-min-w-0 tw-flex-1">
                <p className="tw-text-sm tw-font-bold tw-text-gray-900 tw-m-0 tw-truncate">
                  {fullName || userData?.email || 'Пользователь'}
                </p>
                <p className="tw-text-xs tw-text-gray-400 tw-m-0 tw-truncate">{userData?.email}</p>
              </div>
              {tariffInfo && (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-blue-50 tw-text-blue-700 tw-border tw-border-blue-100 tw-flex-shrink-0 tw-whitespace-nowrap">
                  <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-blue-500" />
                  {tariffInfo.name}
                </span>
              )}
            </div>

            {/* Tariff expiry (only when active) */}
            {tariffInfo && endDate && (
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-2.5 tw-bg-blue-50 tw-border-b tw-border-blue-100">
                <p className="tw-text-xs tw-text-blue-600 tw-m-0">
                  Тариф активен до{' '}
                  <span className="tw-font-bold">{moment(endDate).format('D MMM YYYY')}</span>
                </p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="tw-text-xs tw-font-semibold tw-text-blue-700 hover:tw-underline tw-whitespace-nowrap"
                >
                  Сменить →
                </button>
              </div>
            )}

            {/* Form */}
            <div className="tw-px-5 tw-pt-4 tw-pb-5 tw-space-y-0.5">
              <Input form={form} name="fio" label="Имя Фамилия" classWrap="mb-20" />
              <Input disabled form={form} name="email" label="Почта" classWrap="mb-20" />
              <Input disabled form={form} name="phone_number" label="Номер телефона" classWrap="mb-0" rules={{ required: false }} />
              <div className="tw-pt-4">
                <button
                  onClick={form.handleSubmit(onSubmit)}
                  disabled={userUpdateLoading}
                  className="tw-px-5 tw-py-2 tw-bg-gradient-to-r tw-from-blue-600 tw-to-indigo-600 tw-text-white tw-text-sm tw-font-semibold tw-rounded-xl hover:tw-opacity-90 tw-transition-opacity disabled:tw-opacity-50"
                >
                  {userUpdateLoading ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </Card>

          {/* RIGHT — Telegram + Actions stacked (narrower) */}
          <div className="sm:tw-col-span-2 tw-flex tw-flex-col tw-gap-4">

            {/* Telegram */}
            <Card>
              <CardHeader>
                <svg className="tw-w-4 tw-h-4 tw-text-sky-500 tw-flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-2.012 9.477c-.145.658-.537.818-1.084.508l-3-2.21-1.448 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.876.744z"/>
                </svg>
                <span className="tw-text-sm tw-font-semibold tw-text-gray-800">Телеграм</span>
              </CardHeader>
              <div className="tw-px-5 tw-py-4">
                {hasTelegram ? (
                  <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2.5 tw-rounded-xl tw-bg-green-50 tw-border tw-border-green-100">
                    <svg className="tw-w-4 tw-h-4 tw-text-green-600 tw-flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <div>
                      <p className="tw-text-xs tw-font-semibold tw-text-green-800 tw-m-0">Привязан</p>
                      <p className="tw-text-xs tw-text-green-600 tw-m-0">Уведомления включены</p>
                    </div>
                  </div>
                ) : (
                  <div className="tw-space-y-4">
                    <p className="tw-text-xs tw-text-gray-500 tw-leading-relaxed tw-m-0">
                      Привяжите Telegram.
                    </p>

                    {/* QR code option */}
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-p-3 tw-rounded-xl tw-bg-gray-50 tw-border tw-border-gray-200">
                      {linkTokenData ? (
                        <>
                          <QRCodeSVG
                            value={`https://t.me/${linkTokenData.bot_name}?start=${linkTokenData.token}`}
                            size={120}
                            bgColor="#f9fafb"
                            fgColor="#1d4ed8"
                            level="M"
                          />
                          <p className="tw-text-xs tw-text-gray-400 tw-m-0 tw-text-center">
                            Наведите камеру телефона на QR-код
                          </p>
                          <button
                            onClick={() => dispatch(userTelegramLinkToken())}
                            className="tw-text-xs tw-text-blue-500 hover:tw-underline"
                          >
                            Обновить QR
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => dispatch(userTelegramLinkToken())}
                          disabled={linkTokenLoading}
                          className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-blue-700 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded-xl hover:tw-bg-blue-100 tw-transition-colors disabled:tw-opacity-50"
                        >
                          <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"/>
                          </svg>
                          {linkTokenLoading ? 'Генерация...' : 'Показать QR-код'}
                        </button>
                      )}
                    </div>

                    {/* Widget option */}
                    <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                      <p className="tw-text-xs tw-text-gray-400 tw-m-0">или войдите через Telegram</p>
                      <TelegramLoginButton
                        botName="sellstat_bot"
                        dataOnauth={(user) => dispatch(userTelegram(user))}
                        className="telegram-btn"
                      />
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Quick actions */}
            <Card>
              <CardHeader>
                <svg className="tw-w-4 tw-h-4 tw-text-gray-500 tw-flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <span className="tw-text-sm tw-font-semibold tw-text-gray-800">Действия</span>
              </CardHeader>
              <div className="tw-px-5 tw-py-4 tw-flex tw-flex-col tw-gap-2">
                <button
                  onClick={() => navigate('/account/reset-password')}
                  className="tw-flex tw-items-center tw-gap-2.5 tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl hover:tw-bg-gray-100 tw-transition-colors tw-text-left"
                >
                  <svg className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
                  </svg>
                  Сброс пароля
                </button>
                {tariffInfo && (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="tw-flex tw-items-center tw-gap-2.5 tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-red-600 tw-bg-red-50 tw-border tw-border-red-100 tw-rounded-xl hover:tw-bg-red-100 tw-transition-colors tw-text-left"
                  >
                    <svg className="tw-w-4 tw-h-4 tw-flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                    Сбросить тариф
                  </button>
                )}
              </div>
            </Card>

          </div>
        </div>

        {/* ── ROW 2: Subscriptions (full width) ── */}
        <Card>
          <CardHeader>
            <svg className="tw-w-4 tw-h-4 tw-text-gray-500 tw-flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
            </svg>
            <span className="tw-text-sm tw-font-semibold tw-text-gray-800">Мои подписки</span>
            <div className="tw-ml-auto">
              <img
                src="https://kaspi.kz/img/Logo.svg"
                alt="Kaspi"
                className="tw-h-5 tw-w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          </CardHeader>
          <div className="tw-px-5 tw-py-5">
            <SubscriptionSection />
          </div>
        </Card>

      </div>

      {(userUpdateLoading || tariffPaymentLoading) && <Loading />}
    </>
  );
};

export default AccountPage;
